import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../styles/AutoSendToCrmSettings.css';
import { Button, Dropdown, ExitScreen, Input, Modal, Switch } from './';
import { Warning } from '../assets/icons';
import { colour } from '../styles/GlobalStyles';
import { cloudFunctionV2Auth, recordEvent, serverNow, setCompanyFeature } from '../functions';
import {
    AUTO_SEND_DEFAULT_MIN_PERCENT,
    AUTO_SEND_MIN_PERCENT_BOUNDS,
    autoSendToDate,
    isValidAutoSendPercent,
} from '../utils/autoSendToCrm';

// "Auto-send new deals to buyers" — the company switch behind
// functions/autoSendToCrm.js. OFF by default. Turning it on requires a pipeline,
// a stage, a match-percentage bar, and an explicit acknowledgement in a warning
// modal, because from then on every deal that finishes analysis is sent to its
// matching buyers (email + text via the GHL workflow) with nobody reviewing it.
// Turning it OFF is one click and takes effect on the next lead — the backend
// reads the switch fresh on every candidate.
//
// Stored at companies/{id}.data.features.autoSendToCrm:
//   { enabled, minMatchPercent, pipelineId, pipelineName, stageId, stageName,
//     enabledAt (server clock — the backend only sends leads created AFTER it),
//     enabledBy, disabledAt?, disabledBy?, updatedAt? }
//
// Props: companyId, companyData (from useCompany), refreshCompany, userId

const fmtWhen = (v) => {
    const d = autoSendToDate(v);
    return d ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null;
};

function AutoSendToCrmSettings({ companyId, companyData, refreshCompany, userId }) {
    const stored = companyData?.features?.autoSendToCrm || null;
    const isOn = stored?.enabled === true;
    const hasSlug = !!companyData?.companySlug;

    // Draft config (what the dropdowns/input show). Re-seeded from the stored
    // config whenever it changes (after a save + refreshCompany).
    const [draft, setDraft] = useState({
        pipelineId: stored?.pipelineId || '',
        stageId: stored?.stageId || '',
        minMatchPercent: String(stored?.minMatchPercent ?? AUTO_SEND_DEFAULT_MIN_PERCENT),
    });
    useEffect(() => {
        setDraft({
            pipelineId: stored?.pipelineId || '',
            stageId: stored?.stageId || '',
            minMatchPercent: String(stored?.minMatchPercent ?? AUTO_SEND_DEFAULT_MIN_PERCENT),
        });
    }, [stored?.pipelineId, stored?.stageId, stored?.minMatchPercent]);

    // Switch flipped on but not yet confirmed — config is shown, nothing saved.
    const [arming, setArming] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [acknowledged, setAcknowledged] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Pipelines/stages from the connected GHL account (same endpoint + shape the
    // Buyer Matches page uses). Fetched only when the config is actually shown.
    const [pipelines, setPipelines] = useState([]);
    const [pipelinesState, setPipelinesState] = useState('idle'); // idle | loading | ready | error
    const [pipelineOpen, setPipelineOpen] = useState(false);
    const [stageOpen, setStageOpen] = useState(false);
    const fetchTokenRef = useRef(0);

    const showConfig = isOn || arming;

    const fetchPipelines = useCallback(async () => {
        const url = process.env.REACT_APP_GET_GHL_OPPORTUNITIES;
        if (!url || !companyId) { setPipelinesState('error'); return; }
        const token = ++fetchTokenRef.current;
        setPipelinesState('loading');
        try {
            const res = await cloudFunctionV2Auth(url, { companyId });
            if (token !== fetchTokenRef.current) return;
            if (res?.status !== 200 || !res.data) { setPipelinesState('error'); return; }
            const data = res.data.pipelines || res.data.data?.pipelines || res.data;
            setPipelines(Array.isArray(data) ? data.map((p) => ({
                id: p.id,
                name: p.name || 'Unnamed Pipeline',
                stages: (Array.isArray(p.stages) ? p.stages : []).map((s) => ({ id: s.id, name: s.name || 'Unnamed Stage' })),
            })) : []);
            setPipelinesState('ready');
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Error fetching GHL pipelines for auto-send:', err);
            if (token === fetchTokenRef.current) setPipelinesState('error');
        }
    }, [companyId]);

    useEffect(() => {
        if (showConfig && pipelinesState === 'idle') fetchPipelines();
    }, [showConfig, pipelinesState, fetchPipelines]);

    const activePipeline = useMemo(() => pipelines.find((p) => p.id === draft.pipelineId) || null, [pipelines, draft.pipelineId]);
    const activeStages = activePipeline?.stages || [];
    const activeStage = activeStages.find((s) => s.id === draft.stageId) || null;
    // Names for the summary line: live names when we have them, else what was stored.
    const pipelineName = activePipeline?.name || (draft.pipelineId === stored?.pipelineId ? stored?.pipelineName : null) || null;
    const stageName = activeStage?.name || (draft.stageId === stored?.stageId ? stored?.stageName : null) || null;
    // The stored pipeline/stage vanished from GHL — surface it, since the backend
    // will mark every new lead failed until it's fixed.
    const storedPipelineMissing = isOn && pipelinesState === 'ready' && !!stored?.pipelineId && !pipelines.some((p) => p.id === stored.pipelineId);
    const storedStageMissing = isOn && pipelinesState === 'ready' && !storedPipelineMissing && !!stored?.stageId
        && !(pipelines.find((p) => p.id === stored.pipelineId)?.stages || []).some((s) => s.id === stored.stageId);

    const thresholdValid = isValidAutoSendPercent(draft.minMatchPercent);
    const draftComplete = !!draft.pipelineId && !!draft.stageId && thresholdValid;
    const draftDirty = isOn && (
        draft.pipelineId !== (stored?.pipelineId || '')
        || draft.stageId !== (stored?.stageId || '')
        || Number(draft.minMatchPercent) !== Number(stored?.minMatchPercent ?? AUTO_SEND_DEFAULT_MIN_PERCENT)
    );

    const persist = async (value, eventName, eventFields) => {
        setSaving(true); setError(null);
        try {
            const res = await setCompanyFeature(companyId, 'autoSendToCrm', value);
            if (res?.status !== 200) { setError('Could not save. Please try again.'); return false; }
            recordEvent(eventName, { companyId, ...eventFields });
            if (typeof refreshCompany === 'function') await refreshCompany();
            return true;
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Auto-send save failed:', err);
            setError('Could not save. Please try again.');
            return false;
        } finally {
            setSaving(false);
        }
    };

    const turnOff = async () => {
        const ok = await persist({
            ...(stored || {}),
            enabled: false,
            disabledAt: serverNow(),
            disabledBy: userId || null,
        }, 'auto_send_to_crm_disabled', {});
        if (ok) setArming(false);
    };

    const confirmTurnOn = async () => {
        if (!draftComplete || !acknowledged) return;
        const ok = await persist({
            enabled: true,
            minMatchPercent: Number(draft.minMatchPercent),
            pipelineId: draft.pipelineId,
            pipelineName: pipelineName || null,
            stageId: draft.stageId,
            stageName: stageName || null,
            // Server clock: the backend only auto-sends leads created AFTER this
            // moment, so a browser clock that's off can't widen the window.
            enabledAt: serverNow(),
            enabledBy: userId || null,
        }, 'auto_send_to_crm_enabled', { minMatchPercent: Number(draft.minMatchPercent) });
        if (ok) { setArming(false); setConfirmOpen(false); setAcknowledged(false); }
    };

    // Editing bar/pipeline/stage while ON keeps the ORIGINAL enabledAt — moving it
    // forward would silently exclude leads that arrived in between.
    const saveChanges = async () => {
        if (!isOn || !draftDirty || !draftComplete) return;
        await persist({
            ...stored,
            minMatchPercent: Number(draft.minMatchPercent),
            pipelineId: draft.pipelineId,
            pipelineName: pipelineName || null,
            stageId: draft.stageId,
            stageName: stageName || null,
            updatedAt: serverNow(),
            updatedBy: userId || null,
        }, 'auto_send_to_crm_updated', { minMatchPercent: Number(draft.minMatchPercent) });
    };

    const onSwitch = (checked) => {
        if (saving) return;
        setError(null);
        if (checked) {
            if (isOn) return;
            recordEvent('auto_send_to_crm_arm', { companyId });
            setArming(true);
        } else if (isOn) {
            turnOff();
        } else {
            setArming(false);
        }
    };

    const cancelArming = () => {
        setArming(false);
        setDraft({
            pipelineId: stored?.pipelineId || '',
            stageId: stored?.stageId || '',
            minMatchPercent: String(stored?.minMatchPercent ?? AUTO_SEND_DEFAULT_MIN_PERCENT),
        });
    };

    const pipelineLabel = pipelinesState === 'loading'
        ? 'Loading pipelines…'
        : pipelinesState === 'error'
            ? "Couldn't reach GoHighLevel"
            : (activePipeline?.name || (draft.pipelineId && stored?.pipelineName) || 'Select pipeline…');
    const stageLabel = pipelinesState === 'loading'
        ? 'Loading…'
        : (activeStage?.name || (draft.stageId && stored?.stageName) || (draft.pipelineId ? 'Select stage…' : 'Select pipeline first'));
    const pipelineDisabled = saving || pipelinesState === 'loading' || pipelinesState === 'error';
    const stageDisabled = pipelineDisabled || !draft.pipelineId;

    const enabledOn = fmtWhen(stored?.enabledAt);

    return (
        <div className="integrations-ghl-panel-section autosend-section">
            <div className="integrations-ghl-panel-section-head">
                <span className="body-semibold colour-primary">Auto-send new deals to buyers</span>
                <span className="label-regular colour-secondary">
                    When a new deal finishes analysis, automatically create a GoHighLevel opportunity for every buyer whose buy box matches it at or above your bar — the same thing as pressing “Create Opportunity” on the Buyer Matches page, with the default message and subject line. Off by default.
                </span>
            </div>

            <div className="autosend-switch-row">
                <Switch
                    label={isOn ? 'On' : (arming ? 'Setting up…' : 'Off')}
                    val={isOn || arming}
                    setVal={onSwitch}
                    disabled={saving || (!isOn && !hasSlug)}
                />
                {isOn && (
                    <span className="label-regular colour-secondary">
                        {`Deals matching ${stored?.minMatchPercent ?? AUTO_SEND_DEFAULT_MIN_PERCENT}% or more go to ${stored?.pipelineName || 'your pipeline'} › ${stored?.stageName || 'your stage'} automatically`}
                        {enabledOn ? ` · on since ${enabledOn}` : ''}
                    </span>
                )}
            </div>

            {!hasSlug && !isOn && (
                <div className="autosend-warning">
                    <Warning fill={colour.orange03} />
                    <span className="label-regular colour-primary">
                        You don’t have a buyer signup link yet, so buyers would get no deal link. Set one up on the Buyers page before turning this on.
                    </span>
                </div>
            )}

            {(storedPipelineMissing || storedStageMissing) && (
                <div className="autosend-warning">
                    <Warning fill={colour.orange03} />
                    <span className="label-regular colour-primary">
                        {storedPipelineMissing
                            ? 'The pipeline chosen for auto-send no longer exists in GoHighLevel. New deals will not be sent until you pick another one.'
                            : 'The stage chosen for auto-send no longer exists in GoHighLevel. New deals will not be sent until you pick another one.'}
                    </span>
                </div>
            )}

            {showConfig && (
                <div className="autosend-config">
                    <div className="autosend-config-row">
                        <div className="autosend-select">
                            <span className="label-bold-caps colour-tertiary">
                                Pipeline
                                {pipelinesState === 'error' && (
                                    <button type="button" className="autosend-retry-link label-bold cursor-pointer" onClick={fetchPipelines}>Retry</button>
                                )}
                            </span>
                            <div className="relative-container">
                                <Dropdown
                                    open={pipelineOpen && !pipelineDisabled}
                                    icon={null}
                                    label={pipelineLabel}
                                    disabled={pipelineDisabled}
                                    options={pipelines.map((p) => ({ label: p.name, value: p.id, selected: p.id === draft.pipelineId }))}
                                    multiselect={false}
                                    cta={(option) => {
                                        setDraft((d) => ({ ...d, pipelineId: option.value, stageId: '' }));
                                        setPipelineOpen(false);
                                    }}
                                    sidealign="left"
                                    topalign="bottom"
                                    toggleDropdown={() => !pipelineDisabled && setPipelineOpen(!pipelineOpen)}
                                    indexSelect={false}
                                    selectedIndex={null}
                                />
                                {pipelineOpen && <ExitScreen clickScreen={() => setPipelineOpen(false)} />}
                            </div>
                        </div>
                        <div className="autosend-select">
                            <span className="label-bold-caps colour-tertiary">Stage</span>
                            <div className="relative-container">
                                <Dropdown
                                    open={stageOpen && !stageDisabled}
                                    icon={null}
                                    label={stageLabel}
                                    disabled={stageDisabled}
                                    options={activeStages.map((s) => ({ label: s.name, value: s.id, selected: s.id === draft.stageId }))}
                                    multiselect={false}
                                    cta={(option) => {
                                        setDraft((d) => ({ ...d, stageId: option.value }));
                                        setStageOpen(false);
                                    }}
                                    sidealign="left"
                                    topalign="bottom"
                                    toggleDropdown={() => !stageDisabled && setStageOpen(!stageOpen)}
                                    indexSelect={false}
                                    selectedIndex={null}
                                />
                                {stageOpen && <ExitScreen clickScreen={() => setStageOpen(false)} />}
                            </div>
                        </div>
                        <div className="autosend-select autosend-threshold">
                            <span className="label-bold-caps colour-tertiary">Minimum match</span>
                            <Input
                                label={null}
                                labelType={null}
                                value={draft.minMatchPercent}
                                onChange={(e) => setDraft((d) => ({ ...d, minMatchPercent: e.target.value }))}
                                placeholder={String(AUTO_SEND_DEFAULT_MIN_PERCENT)}
                                type="number"
                                inputMode="numeric"
                                leftUnit={null}
                                rightUnit="%"
                                unitSwitch={false}
                                leftUnitSelected={false}
                                unitSwitchFunc={null}
                                disabled={saving}
                                multiline={false}
                                error={draft.minMatchPercent !== '' && !thresholdValid}
                                info={false}
                                reset={false}
                                prompt={false}
                            />
                        </div>
                    </div>
                    <span className="label-regular colour-tertiary">
                        {`Buyers at or above this match percentage are sent the deal (${AUTO_SEND_MIN_PERCENT_BOUNDS.min}–${AUTO_SEND_MIN_PERCENT_BOUNDS.max}). Your own buy box is never a send target; packages are always sent by hand.`}
                    </span>

                    <div className="autosend-actions">
                        {arming ? (
                            <>
                                <Button
                                    buttonType="primary-button"
                                    size="medium"
                                    text="Turn on auto-send…"
                                    cta={() => { setAcknowledged(false); setConfirmOpen(true); }}
                                    disabled={!draftComplete || saving}
                                    leftIcon={null}
                                    leftIconClass=""
                                    rightIcon={null}
                                    rightIconClass=""
                                    alt=""
                                    standardIcon={false}
                                    badge={false}
                                    badgeVal={null}
                                    badgeBG={null}
                                    buttonRef={null}
                                    spinner={false}
                                />
                                <Button
                                    buttonType="secondary-button"
                                    size="medium"
                                    text="Cancel"
                                    cta={cancelArming}
                                    disabled={saving}
                                    leftIcon={null}
                                    leftIconClass=""
                                    rightIcon={null}
                                    rightIconClass=""
                                    alt=""
                                    standardIcon={false}
                                    badge={false}
                                    badgeVal={null}
                                    badgeBG={null}
                                    buttonRef={null}
                                    spinner={false}
                                />
                            </>
                        ) : (
                            <Button
                                buttonType="primary-button"
                                size="medium"
                                text={saving ? 'Saving…' : 'Save changes'}
                                cta={saveChanges}
                                disabled={!draftDirty || !draftComplete || saving}
                                leftIcon={null}
                                leftIconClass=""
                                rightIcon={null}
                                rightIconClass=""
                                alt=""
                                standardIcon={false}
                                badge={false}
                                badgeVal={null}
                                badgeBG={null}
                                buttonRef={null}
                                spinner={saving}
                            />
                        )}
                    </div>
                </div>
            )}

            {error && <span className="body-regular colour-error">{error}</span>}

            {confirmOpen && (
                <Modal
                    heading="Turn on automatic sending?"
                    subheading={null}
                    body={
                        <div className="autosend-confirm-body">
                            <div className="autosend-warning autosend-warning-strong">
                                <Warning fill={colour.orange03} />
                                <span className="body-bold colour-primary">
                                    Buyers will be contacted with no one reviewing the deal first.
                                </span>
                            </div>
                            <ul className="autosend-confirm-list">
                                <li className="body-regular colour-primary">
                                    {`Every new deal that finishes analysis is sent to every buyer matching ${Number(draft.minMatchPercent)}% or more — automatically, within seconds.`}
                                </li>
                                <li className="body-regular colour-primary">
                                    {`Each buyer gets a GoHighLevel opportunity in ${pipelineName || 'the chosen pipeline'} › ${stageName || 'the chosen stage'}. Your workflow then emails and texts them the deal page link.`}
                                </li>
                                <li className="body-regular colour-primary">
                                    That includes deals with wrong prices, bad photos, or mistaken addresses. Once buyers have been messaged there is no undo.
                                </li>
                                <li className="body-regular colour-primary">
                                    Only deals that arrive after you turn this on are affected — nothing already in Clozers is sent. You can turn it off at any time.
                                </li>
                            </ul>
                            <label className="autosend-ack cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="autosend-ack-checkbox"
                                    checked={acknowledged}
                                    onChange={(e) => setAcknowledged(e.target.checked)}
                                    disabled={saving}
                                />
                                <span className="body-regular colour-primary">
                                    I understand buyers will be contacted automatically without review.
                                </span>
                            </label>
                        </div>
                    }
                    type="destructive"
                    cancelText="Cancel"
                    cancelButtonCTA={() => { if (!saving) setConfirmOpen(false); }}
                    actionButtonText={saving ? 'Turning on…' : 'Turn on auto-send'}
                    actionButtonCTA={confirmTurnOn}
                    secondaryButtonText={null}
                    secondaryButtonCTA={null}
                    exitButtonCTA={() => { if (!saving) setConfirmOpen(false); }}
                    loading={saving}
                    actionButtonDisabled={!acknowledged || !draftComplete || saving}
                    leftIcon={null}
                    rightIcon={null}
                    cancelLeftIcon={null}
                    cancelRightIcon={null}
                    customClass="autosend-confirm-modal"
                />
            )}
        </div>
    );
}

export default AutoSendToCrmSettings;
